/**
 * YetkiGecmisSekmesi.jsx — Yetki Yönetimi › İşlem Geçmişi (backlog #309, 2026-10-10)
 *
 * rbac_islem_gecmisi (V72): bu sayfada ve Kullanıcılar sayfasında yapılan her rol, yetki, kullanıcı rolü,
 * firma bağlantısı ve kök devri değişikliği; kim, ne zaman, önce → sonra. Yalnızca okunur; kayıtlar
 * silinmez. Backend: GET /rbac/history.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Card, CardBody, CardHeader, Row, Col, Button, Alert, Spinner, Input, Label, Table, Badge,
  Pagination, PaginationItem, PaginationLink,
} from "reactstrap";
import { History, X } from "lucide-react";
import { listRbacHistory } from "../../../services/rbacService";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../../i18n";

const PAGE_SIZE = 50;

const ISLEMLER = [
  { value: "ROL_YETKILERI", get label() { return i18n.t("ayarlar:yetkiGecmisSekmesi.rolYetkileri"); } },
  { value: "KULLANICI_ROLU", get label() { return i18n.t("ayarlar:yetkiGecmisSekmesi.kullaniciRolu"); } },
  { value: "KULLANICI_FIRMA", get label() { return i18n.t("ayarlar:yetkiGecmisSekmesi.kullanicininFirmasi"); } },
  { value: "ROL_OLUSTURMA", get label() { return i18n.t("ayarlar:yetkiGecmisSekmesi.rolOlusturma"); } },
  { value: "ROL_ADI", get label() { return i18n.t("ayarlar:yetkiGecmisSekmesi.rolAdi"); } },
  { value: "ROL_ADI_EN", get label() { return i18n.t("ayarlar:yetkiGecmisSekmesi.rolAdiEn"); } },   // #421
  { value: "ROL_SAYFA_ERISIMI", get label() { return i18n.t("ayarlar:yetkiGecmisSekmesi.rolSayfaErisimi"); } },
  { value: "KOK_DEVRI", get label() { return i18n.t("ayarlar:yetkiGecmisSekmesi.kokDevri"); } },
  // #413 (2026-10-13): the portal roles' history (V90 writes the first three as the seed; #416 / #417 write the rest).
  ...["PORTAL_ROL_OLUSTURMA", "PORTAL_ROL_DEGISTIRME", "PORTAL_ROL_VARSAYILAN", "PORTAL_ARAYUZ_TOHUM", "PORTAL_ARAYUZ_DEGISTIRME",
    "FIRMA_ROL_ATAMA", "FIRMA_ROL_BITIRME", "FIRMA_KISIT_EKLEME", "FIRMA_KISIT_BITIRME", "FIRMA_ROL_TOHUM",
    // #447 (2026-10-17): an organizer company's tier in one area (Yok / İzleme / Denetim), written by #446
    "ORGANIZATOR_ALAN_KADEME",
    // #456 (2026-10-17): an organizer company's payment link was set, changed or switched off
    "ODEME_BAGLANTISI"].map((value) => ({
    value, get label() { return i18n.t(`ayarlar:yetkiGecmisSekmesi.islemAdlari.${value}`); },
  })),
];

// What a history row's target is, in words (ROL / KULLANICI as before; #413 adds the portal ones).
const HEDEF_ANAHTARI = { ROL: "rol", KULLANICI: "kullanici", PORTAL_ROL: "portalRolu", PORTAL_ARAYUZ: "arayuzSeti", FIRMA: "firma" };

const fmtDate = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString(currentLocaleTag());
};

function parse(json) {
  if (json === null || json === undefined || json === "") return null;
  try {
    return JSON.parse(json);
  } catch {
    return json;
  }
}

/** One change, before → after, in words. Names come from the current role and permission lists. */
function Degisiklik({ item, roleName, permissionName }) {
  const { t } = useTranslation("ayarlar");
  const once = parse(item.onceki);
  const sonra = parse(item.sonraki);
  switch (item.islem) {
    case "ROL_YETKILERI": {
      const a = new Set(Array.isArray(once) ? once : []);
      const b = new Set(Array.isArray(sonra) ? sonra : []);
      const eklenen = [...b].filter((x) => !a.has(x));
      const kaldirilan = [...a].filter((x) => !b.has(x));
      return (
        <div className="d-flex flex-wrap gap-1">
          {eklenen.map((id) => <Badge key={`+${id}`} color="success">+ {permissionName(id)}</Badge>)}
          {kaldirilan.map((id) => <Badge key={`-${id}`} color="danger">− {permissionName(id)}</Badge>)}
          {eklenen.length === 0 && kaldirilan.length === 0 && <span className="text-muted small">{t("yetkiGecmisSekmesi.degisiklikYok")}</span>}
        </div>
      );
    }
    case "KULLANICI_ROLU": {
      const ad = (r) => (r ? `${r.rolAdi ?? `#${r.rolId}`}${r.personel ? " (personel)" : ""}` : "—");
      return <span className="small">{ad(once)} → <span className="fw-semibold">{ad(sonra)}</span></span>;
    }
    case "KULLANICI_FIRMA": {
      const list = (v) => (Array.isArray(v) && v.length ? v.map((id) => `#${id}`).join(", ") : "firma yok");
      return <span className="small font-monospace">{list(once)} → <span className="fw-semibold">{list(sonra)}</span></span>;
    }
    case "ROL_SAYFA_ERISIMI": {
      const ad = (v) => (v === null || v === undefined ? t("yetkiGecmisSekmesi.kendiSayfalari") : t("yetkiGecmisSekmesi.ileAyni", { roleName: roleName(v) }));
      return <span className="small">{ad(once)} → <span className="fw-semibold">{ad(sonra)}</span></span>;
    }
    case "ROL_ADI":
    case "ROL_ADI_EN":   // #421: the English name, before → after (blank = none)
      return <span className="small">{String(once ?? "—")} → <span className="fw-semibold">{String(sonra ?? "—")}</span></span>;
    case "ROL_OLUSTURMA":
      return <span className="small">{t("yetkiGecmisSekmesi.sablon", { ad: sonra?.ad })}{" "}{sonra?.sablonRolAdi ?? `#${sonra?.sablonRolId}`}</span>;
    case "KOK_DEVRI":
      return <span className="small">{t("yetkiGecmisSekmesi.kok", { kokUserId: once?.kokUserId })}{" "}<span className="fw-semibold">#{sonra?.kokUserId}</span></span>;
    // #413: the V90 seed rows.
    case "PORTAL_ROL_OLUSTURMA":
      return <span className="small">{t("yetkiGecmisSekmesi.portalRolOlusturma", {
        ad: sonra?.ad, kod: sonra?.kod, moduller: Object.keys(sonra?.moduller ?? {}).length, eylemler: (sonra?.eylemler ?? []).length })}</span>;
    case "PORTAL_ROL_VARSAYILAN":
      return <span className="small">{t("yetkiGecmisSekmesi.portalRolVarsayilan", { kod: sonra?.portalRolKod })}</span>;
    case "PORTAL_ARAYUZ_TOHUM":
      return <span className="small">{t("yetkiGecmisSekmesi.arayuzTohum", { satir: sonra?.satirSayisi, kaynak: sonra?.kaynak })}</span>;
    // #416 / #417: what the staff API writes (portal roles, set caps, a login's roles in a company, a company's restrictions).
    case "PORTAL_ROL_DEGISTIRME": {
      if (sonra?.silindi) return <span className="small">{t("yetkiGecmisSekmesi.portalRolSilindi", { ad: once?.ad, kod: once?.kod })}</span>;
      const seviye = (s) => t(`portalArayuzleri.seviye.${s ?? "NONE"}`);
      const parcalar = [];
      const modO = once?.moduller ?? {};
      for (const m of Object.keys(sonra?.moduller ?? {})) {
        parcalar.push(`${t(`portalArayuzleri.modul.${m}`)}: ${seviye(modO[m])} → ${seviye(sonra.moduller[m])}`);
      }
      if (Array.isArray(sonra?.eylemler)) {
        const a = new Set(Array.isArray(once?.eylemler) ? once.eylemler : []);
        const b = new Set(sonra.eylemler);
        for (const e of b) if (!a.has(e)) parcalar.push(`+ ${t(`portal:organizator.eylem.${e}`, { defaultValue: e })}`);
        for (const e of a) if (!b.has(e)) parcalar.push(`− ${t(`portal:organizator.eylem.${e}`, { defaultValue: e })}`);
      }
      for (const k of ["ad", "adEn", "aciklama", "aciklamaEn", "sira", "aktif"]) {
        if (sonra && k in sonra) parcalar.push(`${k}: ${String(once?.[k] ?? "—")} → ${String(sonra[k] ?? "—")}`);
      }
      return <span className="small">{parcalar.length ? parcalar.join(" · ") : t("yetkiGecmisSekmesi.degisiklikYok")}</span>;
    }
    case "PORTAL_ARAYUZ_DEGISTIRME":
      return (
        <span className="small">
          {t(sonra?.yabanci ? "portalArayuzleri.yabanci" : "portalArayuzleri.yerli")} {t(`portal:arayuz.${sonra?.rol}`, { defaultValue: sonra?.rol })} · {t(`portalArayuzleri.modul.${sonra?.modul}`, { defaultValue: sonra?.modul })}:{" "}
          {t(`portalArayuzleri.seviye.${once?.seviye ?? "NONE"}`)} → <span className="fw-semibold">{t(`portalArayuzleri.seviye.${sonra?.seviye ?? "NONE"}`)}</span>
        </span>
      );
    case "FIRMA_ROL_ATAMA":
      return <span className="small">{t("yetkiGecmisSekmesi.firmaRolAtama", { kullanici: sonra?.userId, rol: sonra?.portalRolKod ?? `#${sonra?.portalRolId}` })}</span>;
    case "FIRMA_ROL_BITIRME":
      return <span className="small">{t("yetkiGecmisSekmesi.firmaRolBitirme", { kullanici: once?.userId, rol: once?.portalRolKod ?? `#${once?.portalRolId}` })}</span>;
    case "FIRMA_KISIT_EKLEME":
    case "FIRMA_KISIT_BITIRME": {
      const k = item.islem === "FIRMA_KISIT_EKLEME" ? sonra : once;
      return (
        <span className="small">
          {t(item.islem === "FIRMA_KISIT_EKLEME" ? "yetkiGecmisSekmesi.kisitEklendi" : "yetkiGecmisSekmesi.kisitKaldirildi", {
            modul: t(`portalArayuzleri.modul.${k?.modul}`, { defaultValue: k?.modul }),
            seviye: t(`portalArayuzleri.seviye.${k?.seviye ?? "NONE"}`),
            fuar: k?.fuarId == null ? t("portalKisitlari.hepsi") : t("portalKisitlari.fuar", { id: k.fuarId }),
          })}
          {k?.gerekce ? `: ${k.gerekce}` : ""}
        </span>
      );
    }
    case "FIRMA_ROL_TOHUM":
      return <span className="small">{t("yetkiGecmisSekmesi.rolTohum", { kod: sonra?.kod, adet: sonra?.baglantiSayisi })}</span>;
    case "ODEME_BAGLANTISI": {
      // #456: {fuarId (0 = every fair), url, etiket, aktif}, before (null for a new link) and after
      const k = sonra ?? once;
      let host = "";
      try { host = new URL(k?.url).hostname; } catch { host = String(k?.url ?? ""); }
      return (
        <span className="small">
          {t("yetkiGecmisSekmesi.odemeBaglantisi", {
            kapsam: k?.fuarId ? t("yetkiGecmisSekmesi.odemeFuar", { id: k.fuarId }) : t("yetkiGecmisSekmesi.odemeTumFuarlar"),
            host,
            durum: k?.aktif ? t("yetkiGecmisSekmesi.odemeAcik") : t("yetkiGecmisSekmesi.odemeKapali"),
          })}
        </span>
      );
    }
    case "ORGANIZATOR_ALAN_KADEME": {
      // #447: one tier changed ({alan, kademe} before and after), or the V96 summary row ({kaynak: 'V96', alanlar: {AREA: TIER}})
      const alanAdi = (a) => t(`yetkiGecmisSekmesi.alanAdi.${a}`, { defaultValue: a });
      const kademeAdi = (k) => t(`yetkiGecmisSekmesi.kademe.${k ?? "NONE"}`, { defaultValue: k });
      if (sonra?.kaynak === "V96") {
        return (
          <span className="small">
            {t("yetkiGecmisSekmesi.alanKademeV96", {
              alanlar: Object.entries(sonra.alanlar ?? {}).map(([a, k]) => `${alanAdi(a)}: ${kademeAdi(k)}`).join(", "),
            })}
          </span>
        );
      }
      return <span className="small">{t("yetkiGecmisSekmesi.alanKademe", { alan: alanAdi(sonra?.alan ?? once?.alan), once: kademeAdi(once?.kademe), sonra: kademeAdi(sonra?.kademe) })}</span>;
    }
    default:
      return <span className="small font-monospace">{item.onceki} → {item.sonraki}</span>;
  }
}

/**
 * @param hedef  optional {hedefTipi, hedefId, ad} — opened from a user's or role's "Geçmiş" button
 */
export default function YetkiGecmisSekmesi({ roles, permissions, hedef, onClearHedef }) {
  const { t } = useTranslation("ayarlar");
  const [islem, setIslem] = useState("");
  const [hedefTipi, setHedefTipi] = useState("");
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const roleName = useMemo(() => {
    const byId = new Map(roles.map((r) => [r.userGroupId, r.userGroupName]));
    return (id) => byId.get(Number(id)) ?? `#${id}`;
  }, [roles]);
  const permissionName = useMemo(() => {
    const byId = new Map(permissions.map((p) => [p.permissionId, p.permissionName]));
    return (id) => byId.get(Number(id)) ?? `#${id}`;
  }, [permissions]);

  const params = useMemo(() => ({
    hedefTipi: hedef ? hedef.hedefTipi : hedefTipi,
    hedefId: hedef ? hedef.hedefId : undefined,
    islem,
    page,
    size: PAGE_SIZE,
  }), [hedef, hedefTipi, islem, page]);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const out = await listRbacHistory(params);
        if (!ignore) setData(out);
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [params]);

  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Card>
      <CardHeader className="d-flex align-items-center gap-2 flex-wrap">
        <History size={16} className="text-muted" />
        <h5 className="card-title mb-0 flex-grow-1">{t("yetkiGecmisSekmesi.islemGecmisi")}</h5>
        <span className="text-muted small">{loading ? t("yetkiGecmisSekmesi.yukleniyor") : t("yetkiGecmisSekmesi.kayit", { total })}</span>
      </CardHeader>
      <CardBody>
        <Row className="g-2 mb-3 align-items-end">
          {hedef ? (
            <Col md={6}>
              <div className="small">
                {t("yetkiGecmisSekmesi.yalnizca")}{" "}<span className="fw-semibold">{hedef.ad}</span>{" "}
                {/* WAS (until #413): ({hedef.hedefTipi === "ROL" ? t("yetkiGecmisSekmesi.rol") : t("yetkiGecmisSekmesi.kullanici")} #{hedef.hedefId}) */}
                <span className="text-muted">({t(`yetkiGecmisSekmesi.${HEDEF_ANAHTARI[hedef.hedefTipi] ?? "kullanici"}`)} #{hedef.hedefId})</span>
                <Button size="sm" color="link" className="p-0 ms-2" onClick={() => { setPage(0); onClearHedef?.(); }}>
                  <X size={13} />{" "}{t("yetkiGecmisSekmesi.tumunuGoster")}
                </Button>
              </div>
            </Col>
          ) : (
            <Col md={3}>
              <Label for="gc-hedef" className="form-label small mb-1">{t("yetkiGecmisSekmesi.hedef")}</Label>
              <Input id="gc-hedef" type="select" bsSize="sm" value={hedefTipi} onChange={(e) => { setHedefTipi(e.target.value); setPage(0); }}>
                <option value="">{t("yetkiGecmisSekmesi.hepsi")}</option>
                <option value="ROL">{t("yetkiGecmisSekmesi.roller")}</option>
                <option value="KULLANICI">{t("yetkiGecmisSekmesi.kullanicilar")}</option>
                <option value="PORTAL_ROL">{t("yetkiGecmisSekmesi.portalRolleri")}</option>
                <option value="PORTAL_ARAYUZ">{t("yetkiGecmisSekmesi.arayuzSetleri")}</option>
                <option value="FIRMA">{t("yetkiGecmisSekmesi.firmalar")}</option>
              </Input>
            </Col>
          )}
          <Col md={3}>
            <Label for="gc-islem" className="form-label small mb-1">{t("yetkiGecmisSekmesi.islem")}</Label>
            <Input id="gc-islem" type="select" bsSize="sm" value={islem} onChange={(e) => { setIslem(e.target.value); setPage(0); }}>
              <option value="">{t("yetkiGecmisSekmesi.hepsi")}</option>
              {ISLEMLER.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
            </Input>
          </Col>
        </Row>
        {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}
        <div className="table-responsive">
          <Table className="align-middle mb-0" size="sm">
            <thead className="table-light">
              <tr>
                <th style={{ width: 160 }}>{t("yetkiGecmisSekmesi.tarih")}</th>
                <th style={{ width: 160 }}>{t("yetkiGecmisSekmesi.islem")}</th>
                <th>{t("yetkiGecmisSekmesi.hedef")}</th>
                <th>{t("yetkiGecmisSekmesi.degisiklik")}</th>
                <th>{t("yetkiGecmisSekmesi.yapan")}</th>
              </tr>
            </thead>
            <tbody>
              {loading && !data && (
                <tr><td colSpan={5} className="text-center py-3"><Spinner color="primary" size="sm" /></td></tr>
              )}
              {data && data.items.length === 0 && (
                <tr><td colSpan={5} className="text-center text-muted py-3">{t("yetkiGecmisSekmesi.kayitYok")}</td></tr>
              )}
              {(data?.items ?? []).map((item) => (
                <tr key={item.id}>
                  <td className="small text-muted">{fmtDate(item.islemTarihi)}</td>
                  {/* WAS (until #413): <td className="small fw-semibold">{item.islemAdi}</td> -- the backend's Turkish label; the code's own translation wins */}
                  <td className="small fw-semibold">{t(`yetkiGecmisSekmesi.islemAdlari.${item.islem}`, { defaultValue: item.islemAdi })}</td>
                  <td className="small">
                    {item.hedefAdi || "—"}{" "}
                    <span className="text-muted font-monospace">
                      {/* WAS (until #413): {item.hedefTipi === "ROL" ? t("yetkiGecmisSekmesi.rol") : t("yetkiGecmisSekmesi.kullanici")} #{item.hedefId} */}
                      {t(`yetkiGecmisSekmesi.${HEDEF_ANAHTARI[item.hedefTipi] ?? "kullanici"}`)} #{item.hedefId}
                    </span>
                    {item.hedefKullaniciAdi && <div className="text-muted font-monospace">{item.hedefKullaniciAdi}</div>}
                  </td>
                  <td>
                    <Degisiklik item={item} roleName={roleName} permissionName={permissionName} />
                    {item.aciklama && <div className="text-muted small">{item.aciklama}</div>}
                  </td>
                  <td className="small">
                    {item.yapanAdi || item.yapanKullaniciAdi || `#${item.yapanUserId}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        {total > PAGE_SIZE && (
          <div className="d-flex align-items-center justify-content-between pt-2">
            <span className="text-muted small">{t("yetkiGecmisSekmesi.sayfa", { v: page + 1 })}{" "}{pageCount}</span>
            <Pagination size="sm" className="mb-0">
              <PaginationItem disabled={page === 0}>
                <PaginationLink previous onClick={() => setPage((p) => Math.max(0, p - 1))} />
              </PaginationItem>
              <PaginationItem disabled={page + 1 >= pageCount}>
                <PaginationLink next onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} />
              </PaginationItem>
            </Pagination>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
